import React, { useContext, useEffect, useState, useRef } from "react";
import CustomTable from "../../../common/customTable/CustomTable";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { ClaimsContext } from "../../../services/ClaimsService";
import { ClaimType } from "../../../constants/claims";
import { Button, COLORS, Modal } from "abs-ui-components";
import {
	getUKETSReportSummary,
	saveUKETSAerReports,
	sendUKETSAerToCompany,
	getUKETSAllShips
} from "../../../services/UKETSAerService";
import { useSelector } from "react-redux";


const COLUMNS = [
	{
		accessorKey: "para",
		header: "Para",
		size: 100,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		 muiTableBodyCellProps: {
            sx: {
                fontWeight: "bold !important",
            },
        },
	},
	{
		accessorKey: "description",
		header: "Description",
		size: 500,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		 muiTableBodyCellProps: {
            sx: {
                fontWeight: "bold !important",
            },
        },
		Cell: ({ cell }) => <span style={{ color: "#1565c0" }}>{cell.getValue()}</span>,
	},
	{
		accessorKey: "co2Ets",
		header: "CO2 ETS",
		size: 130,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
	},
	{
		accessorKey: "ch4Ets",
		header: "CH4 ETS",
		size: 130,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
	},
	{
		accessorKey: "n2oEts",
		header: "N2O ETS",
		size: 130,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
	},
	{
		accessorKey: "meEts",
		header: (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					textAlign: "center",
				}}
			>
				<div>ME ETS</div>
				<div>(CO2eq - m tonnes)</div>
			</div>
		),
		size: 150,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
	},
];

const UKETSReportSummary = ({
	aerDetails,
	ukEtsAerMstId,
	loadDetails,
	status
}) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);

	const [showSubmitToAbsModal, setShowSubmitToAbsModal] =
		useState(false);

	const [showSendToCompanyModal, setShowSendToCompanyModal] =
		useState(false);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const { userHasClaim } = useContext(ClaimsContext);


	const statuses =
		 ["Verification Completed", "No Data Submitted", "Under Revision"];
    
	const [selectedReportMstIds, setSelectedReportMstIds] = useState([]);

    const canSubmitToAbs =
	(status === "No Data Submitted" || status === "Under Revision") &&
	userHasClaim(ClaimType.SUBMIT_REPORT);
    const user = useSelector((state) => state.userInfo.userData);
	const currentUserRole = user ? user?.roles : null; 
	const ALLOWED_ROLES = ['ER_SUPERADMIN','ER_REVIEWER','ER_VERIFIER'];
	const hasAllowedRole = currentUserRole.some(role => ALLOWED_ROLES.includes(role));
	 const shipsRequestRef = useRef({
    id: null,
    promise: null,
});

const [isLoadingShipIds, setIsLoadingShipIds] = useState(false);

	const buildTablePayload = (
		globalFilter,
		pagination = { pageIndex: 0, pageSize: 100 }
	) => ({
		globalFilter: globalFilter ?? "",
		filters: [],
		pagination: {
			page: pagination.pageIndex + 1,
			pageSize: pagination.pageSize,
		},
		sortOption: {
			sortField: "",
			isAscending: true,
		},
		reportYear: 0,
	});
   
	 
const getShipsReportIds = (aerId) => {
    if (
        shipsRequestRef.current.id === aerId &&
        shipsRequestRef.current.promise
    ) {
        return shipsRequestRef.current.promise;
    }

    const payload = {
        listOfShips: buildTablePayload(""),
        eligibleShips: buildTablePayload(""),
    };

    const promise = getUKETSAllShips(aerId, payload)
        .then((response) => {
            const ids = response?.reportMstIds ?? [];

            // Avoid updating state with an older request's response.
            if (shipsRequestRef.current.id === aerId) {
                setSelectedReportMstIds(ids);
            }

            return ids;
        });

    shipsRequestRef.current = {
        id: aerId,
        promise,
    };

    return promise;
};

	const loadData = async () => {
		 if (!ukEtsAerMstId) {
        setData([]);
        setSelectedReportMstIds([]);
        shipsRequestRef.current = {
            id: null,
            promise: null,
        };
        return;
    }

    setLoading(true);

    // Start fetching ship IDs in the background.
    // This does not block the report summary.
    getShipsReportIds(ukEtsAerMstId).catch(() => {
        // The submission handler will handle the error if needed.
    });

		try {
			const result = await getUKETSReportSummary(
				ukEtsAerMstId
			);

			if (!result) {
				setData([]);
				return;
			}
		
			   
			const summaryData = [
				{
					para: "38(1)(b)",
					description:
						"Total aggregated maritime emissions from all maritime activities performed by all ships included within the company-level report.",
					co2Ets:
						result.aggregatedMaritimeEmissions?.co2Ets ?? 0,
					ch4Ets:
						result.aggregatedMaritimeEmissions?.ch4Ets ?? 0,
					n2oEts:
						result.aggregatedMaritimeEmissions?.n2oEts ?? 0,
					meEts:
						result.aggregatedMaritimeEmissions?.meEts ?? 0,
				},
				{
					para: "38(1)(c)",
					description:
						"Details of any emissions reduction claim arising from the use of eligible fuels.",
					co2Ets:
						result.emissionsReductionClaim?.co2Ets ?? 0,
					ch4Ets:
						result.emissionsReductionClaim?.ch4Ets ?? 0,
					n2oEts:
						result.emissionsReductionClaim?.n2oEts ?? 0,
					meEts:
						result.emissionsReductionClaim?.meEts ?? 0,
				},
				{
					para: "38(1)(d)",
					description:
						"Details of any applicable surrender deduction in accordance with paragraph 15(1)(a).",
					co2Ets:
						result.surrenderDeduction?.co2Ets ?? 0,
					ch4Ets:
						result.surrenderDeduction?.ch4Ets ?? 0,
					n2oEts:
						result.surrenderDeduction?.n2oEts ?? 0,
					meEts:
						result.surrenderDeduction?.meEts ?? 0,
				},
				{
					para: "38(1)(e)",
					description:
						"Total maritime emissions after deduction of any applicable surrender deduction.",
					co2Ets:
						result.totalAfterDeduction?.co2Ets ?? 0,
					ch4Ets:
						result.totalAfterDeduction?.ch4Ets ?? 0,
					n2oEts:
						result.totalAfterDeduction?.n2oEts ?? 0,
					meEts:
						result.totalAfterDeduction?.meEts ?? 0,
				},
			];

			setData(summaryData);
		} catch (err) {
			emitToasterMsg(
				"error",
				"Error",
				"Failed to load report summary."
			);

			setData([]);
		} finally {
			setLoading(false);
		}
	};
    

	useEffect(() => {
		loadData();
	}, [ukEtsAerMstId]);

	/**
	 * Submit UK ETS AER to ABS.
	 */

const handleSubmitToAbs = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setIsLoadingShipIds(true);

    try {
        // Wait for the ships API if it is still running.
        const reportMstIds = await getShipsReportIds(ukEtsAerMstId);

        setIsLoadingShipIds(false);

        const result = await saveUKETSAerReports(
            Number(ukEtsAerMstId),
            reportMstIds,
            true
        );

        if (result?.isSuccess) {
            emitToasterMsg(
                "success",
                "Submitted",
                "Report submitted to ABS successfully."
            );

            setShowSubmitToAbsModal(false);

            if (loadDetails) {
                await loadDetails();
            }
        } else {
            emitToasterMsg(
                "error",
                "Submission Failed",
                result?.errorMessage ||
                    "Unable to submit the report to ABS."
            );
        }
    } catch (err) {
        emitToasterMsg(
            "error",
            "Error",
            "Unable to load ship IDs or submit the report. Please try again."
        );
    } finally {
        setIsSubmitting(false);
        setIsLoadingShipIds(false);
    }
};


	/**
	 * Send UK ETS AER back to company for revision.
	 */
	const handleSendToCompany = async () => {
		if (isSubmitting) return;
        const remarks = "remarks"
		setIsSubmitting(true);

		try {
			const result = await sendUKETSAerToCompany(
				Number(ukEtsAerMstId),
				remarks
			);

			if (result?.isSuccess) {
				emitToasterMsg(
					"success",
					"Sent",
					"Report sent back to company for revision."
				);

				setShowSendToCompanyModal(false);

				if (loadDetails) {
					await loadDetails();
				}
			} else {
				emitToasterMsg(
					"error",
					"Action Failed",
					result?.errorMessage ||
						"Unable to send report back to company."
				);
			}
		} catch (err) {
			emitToasterMsg(
				"error",
				"Error",
				err.message
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="p-3">
			<CustomTable
				renderTableData={data}
				columnsData={COLUMNS}
				customProps={{
					header: <strong>Report Summary · Para 38(1)(b) to 38(1)(e)</strong>,
					enableToptoolbar: true,
					enableSort: false,
					enableColumnVirtualization: false,
					enableRowVirtualization: false,
					isLoading: loading,
					tableName: "report-summary",
					  decimalPlaces: 5,
				}}
			/>

			{!loading && data.length === 0 && (
				<div
					style={{
						textAlign: "center",
						padding: "40px",
						color: "#888",
						fontSize: "14px",
					}}
				>
					No summary data available. Please select ships in the List of Ships tab to generate the company-level summary.
				</div>
			)}

			<div className="d-flex justify-content-end p-4" style={{ gap: "8px" }}>
				{canSubmitToAbs ? (
					<Button
						onClick={() => setShowSubmitToAbsModal(true)}
						primaryColor={COLORS.RED.DARK}
						primaryBackgroundColor={COLORS.RED.LIGHT}
						disabled={isSubmitting}
					>
						Submit UK ETS AER to ABS
					</Button>
				) : (
					<Button disabled={!canSubmitToAbs}>UK ETS AER Submitted to ABS</Button>
				)}

				{hasAllowedRole && (
					<Button
						onClick={() => setShowSendToCompanyModal(true)}
						primaryColor={COLORS.RED.DARK}
						primaryBackgroundColor={COLORS.RED.LIGHT}
						disabled={statuses.includes(aerDetails?.status)}
					>
						Submit UK ETS AER to Company
					</Button>
				)}
			</div>

			{/* =========================================================
			    Submit to ABS Confirmation Modal
			========================================================= */}
			<Modal
				isOpen={showSubmitToAbsModal}
				modalHeader
				padding="70px"
				headerHeight="50px"
				showFooter
				onCloseClickListener={() => setShowSubmitToAbsModal(false)}
				footer={
					<div className="d-flex justify-content-end p-4" style={{ gap: "8px" }}>
						<Button disabled={isSubmitting} onClick={handleSubmitToAbs} primaryColor={COLORS.RED.DARK} primaryBackgroundColor={COLORS.RED.LIGHT}>
							{isLoadingShipIds ? "Submitting..." : isSubmitting ? "Submitting..." : "Submit"}
						</Button>

						<Button variant="secondary" disabled={isSubmitting} onClick={() => setShowSubmitToAbsModal(false)}>
							Cancel
						</Button>
					</div>
				}
			>
				<div>
					Are you sure you want to submit this UK ETS Annual Emission Report to ABS?
					<br />
					<br />
					Once submitted, the report will be locked for editing.
				</div>
			</Modal>

			{/* =========================================================
			    Send to Company Confirmation Modal
			========================================================= */}
			<Modal
				isOpen={showSendToCompanyModal}
				modalHeader
				padding="70px"
				headerHeight="50px"
				showFooter
				onCloseClickListener={() => setShowSendToCompanyModal(false)}
				footer={
					<div className="d-flex justify-content-end p-4" style={{ gap: "8px" }}>
						<Button disabled={isSubmitting} primaryColor={COLORS.RED.DARK} primaryBackgroundColor={COLORS.RED.LIGHT} onClick={handleSendToCompany}>
							{isSubmitting ? "Sending..." : "Send to Company"}
						</Button>

						<Button variant="secondary" disabled={isSubmitting} onClick={() => setShowSendToCompanyModal(false)}>
							Cancel
						</Button>
					</div>
				}
			>
				<div>
					Are you sure you want to send this report back to the company for revision?
					<br />
					<br />
					The report status will change to <strong>Under Revision</strong> and the company will be able to make corrections before resubmitting.
				</div>
			</Modal>
		</div>
	);
};

export default UKETSReportSummary;